<template>
  <div class="h-full bg-white mt-6 rounded p-3">
    <div class="w-full flex flex-col md:flex-row justify-between items-center border-b py-5 mb-5">
      <div class="flex">
        <div class="w-32 h-32 p-4 -mt-20 ml-3 mb-2 text-white rounded-lg relative flex-no-shrink bg-tertiary mr-5">
          <icon name="remote_viewing"></icon>
        </div>
        <h1 class="font-semibold">Schedule a Viewing</h1>
      </div>
      <button @click="scheduleViewing" :disabled="scheduling" class="w-64 px-5 py-4 flex items-center justify-center bg-tertiary text-white rounded-lg">
        <loader v-if="scheduling" fillColor="white" />
        <template v-else>
          <icon name="schedule" :center="false" class="text-white w-8 h-8 mr-3" />
          <span class="font-semibold uppercase text-sm">Schedule Viewing</span>
        </template>
      </button>
    </div><!-- header end -->

    <div v-if="Object.keys(errors).length > 0" class="p-3 border rounded shadow mb-4">
      <div class="flex items-center text-red mb-4">
        <i class="fas fa-exclamation-triangle mr-3"></i> Please correct the following errors:
      </div>
        <template v-for="errs in errors">
          <ul v-for="error in errs" :key="error" class="pl-6 text-sm leading-normal">
            <li>{{ formatError(error) }}</li>
          </ul>
        </template>
    </div>

    <div class="flex">
      <div class="max-w-sm w-full p-3 mr-10">
        <div class="flex items-center justify-between pb-3 border-b mb-4">
          <div class="flex items-center">
            <icon name="calendar_2" :center="false" class="w-8 h-8 mr-3 text-tertiary" />
            <span class="text-xl mr-4">Date & Time</span>
          </div>
          <button @click="scheduleForNow = !scheduleForNow" class="text-xs font-semibold rounded bg-tertiary text-white p-2 focus:outline-none">
            <div v-if="!scheduleForNow" class="flex items-center ">
              <icon name="clock" class="w-4 h-4 mr-2" :center="false" />
              Schedule for now
            </div>

            <div v-else class="flex items-center ">
              <icon name="calendar" class="w-4 h-4 mr-2" :center="false" />
              Back to calendar
            </div>

          </button>
        </div>
        <calendar v-if="!scheduleForNow" v-model="viewing.date" class="mb-4" />
        <div class="flex items-center justify-center h-16" v-else>
          <span class="font-bold">Today</span>
        </div>

        <time-picker
          v-model="viewing.time"
          :startTime="startTime"
          :fromNow="scheduleForNow"
          class="mx-auto max-w-xs border-b pb-4 mb-4"
        />
        <div class="flex flex-col items-center justify-center">
          <span class="text-sm mb-2">Your selected appointment time:</span>
          <div v-if="viewingTimeRange">
            <span class="text-sm font-semibold mr-2">{{ viewing.date.format('dddd Do MMMM') }}:</span>
            <span v-if="!scheduleForNow" class="text-sm font-semibold">
              {{ viewingTimeRange }}
            </span>
            <span v-else class="text-sm font-semibold">Now</span>
          </div>
        </div>
      </div>

      <div class="max-w-sm w-full flex flex-col p-3 mr-10">
        <div class="flex items-center pb-3 border-b mb-4 flex-no-shrink">
          <icon name="host" :center="false" class="w-8 h-8 mr-3 text-tertiary" />
          <span class="text-xl">Host</span>
        </div>
        <div class="h-full">
          <host v-model="viewing.host" />
        </div>

        <div class="flex items-center pb-3 border-b mb-4">
          <icon name="user" :center="false" class="w-8 h-8 mr-3 text-tertiary" />
          <div class="w-full flex items-center justify-between">
            <span class="text-xl">Attendees</span>
            <span class="w-6 h-6 text-xs flex items-center justify-center rounded-full bg-tertiary text-white">{{ viewing.applicants.length }}</span>
          </div>
        </div>
        <div class="h-full">
          <applicants v-model="viewing.applicants" />
        </div>
      </div>

      <div class="w-full flex flex-col p-3">
        <div class="flex items-center pb-3 border-b mb-4">
          <icon name="properties" :center="false" class="w-8 h-8 mr-3 text-tertiary" />
          <span class="text-xl">Properties</span>
        </div>
        <div class="overflow-y-scroll">
          <property-list
            :primeProperty="viewing.primeProperty"
            :properties="viewing.properties"
            :deletable="true"
            @prime="setPrimeProperty"
            @prime:remove="removePrimeProperty"
            @property:remove="removeProperty"
          />
        </div>

        <button
          class="px-8 py-2 flex items-center justify-center rounded-lg border-2 text-tertiary border-tertiary"
          @click="showPropertySelector = true"
        >
          <icon name="add" :center="false" class="w-6 h-6 mr-2" />
          <span class="">Add Properties</span>
        </button>
      </div>
    </div>
    <modal
      v-if="showPropertySelector"
      width="80%"
      height="600px"
      :showCloseBtn="true"
      @leave="showPropertySelector = false"
    >
      <property-selector v-model="viewingProperties" />
    </modal>
  </div>
</template>

<script>
import api from '@/http'
import Calendar from '@/components/SmartViewing/Calendar'
import Host from '@/components/SmartViewing/Host'
import Applicants from '@/components/SmartViewing/Applicants'
import PropertyList from '@/components/SmartViewing/PropertyList'
import PropertySelector from '@/components/SmartViewing/PropertySelector'
import TimePicker from '@/components/SmartViewing/TimePicker'
import ViewingDetails from '@/components/SmartViewing/ViewingDetails'
import ApplicantList from '@/components/SmartViewing/ApplicantList'
import moment from 'moment'
import { mapState, mapActions } from 'vuex'
import { EventBus } from '@/EventBus'

export default {
  name: 'ScheduleViewing',

  components: {
    Calendar,
    Host,
    Applicants,
    PropertyList,
    ViewingDetails,
    ApplicantList,
    PropertySelector,
    TimePicker
  },

  computed: {
    ...mapState(['selectedOffice', 'profile']),

    startTime () {
      if (this.viewing.date.isSame(moment().local(), 'day')) {
        return moment().local().startOf('hour').set({
          minutes: Math.ceil(moment().local().minutes() / 30) * 30,
          seconds: 0
        })
      } else {
        return moment().local().set({ hour: 8, minutes: 0, seconds: 0 })
      }
    },

    viewingProperties: {
      get () {
        if (this.viewing.primeProperty) {
          return this.viewing.properties.concat(this.viewing.primeProperty)
        }
        return this.viewing.properties
      },

      set (value) {
        this.viewing.primeProperty = value ? value[0] : null
        this.viewing.properties = value.slice(1)
      }
    },

    viewingTimeRange () {
      if (!this.viewing.time.from || (!this.viewing.time.duration || this.viewing.time.duration === 0)) {
        return null
      }

      const end = moment(this.viewing.time.from, 'HH:mm')
        .add(this.viewing.time.duration, 'minutes')

      return `${this.viewing.time.from} - ${end.format('HH:mm')}`
    }
  },

  data () {
    return {
      errors: {},
      selectedProperties: [],
      applicants: [],
      showPropertySelector: false,
      scheduling: false,
      scheduleForNow: false,
      viewing: {
        time: {
          duration: 30,
          from: moment().local().startOf('hour').set({
            minutes: Math.ceil(moment().local().minutes() / 30) * 30,
            seconds: 0
          }).format('HH:mm')
        },
        date: moment().local(),
        propertyCount: 0,
        host: {
          name: '',
          phone: '',
          email: ''
        },
        primeProperty: null,
        properties: [],
        applicants: [
          { applicant_name: '', applicant_phone: '', applicant_email: '' }
        ]
      }
    }
  },

  methods: {
    ...mapActions(['displayToastNotification']),

    scheduleViewing () {
      this.errors = {}
      this.scheduling = true

      if (this.scheduleForNow) {
        this.viewing.time.from = moment().local().format('HH:mm')
      }

      api.post('smart-viewing', {
        office_id: this.selectedOffice.id,
        host_name: this.viewing.host.name,
        host_phone: this.viewing.host.phone,
        host_email: this.viewing.host.email,
        applicants: this.viewing.applicants,
        properties: this.viewing.properties.map(p => p.id),
        appointment_at: `${this.viewing.date.format('YYYY-MM-DD')} ${this.viewing.time.from}:00`,
        appointment_duration: this.viewing.time.duration,
        prime_property_id: this.viewing.primeProperty ? this.viewing.primeProperty.id : null
      }).then(() => {
        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check',
          title: 'Smart Viewing Scheduled',
          info: 'Your smart viewing has been scheduled and your applicants have been notified.'
        })
        EventBus.$emit('page:change-tab', 'viewings')
      }).catch(({ response }) => {
        if (response.data.errors) {
          this.errors = response.data.errors
        } else {
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-exclamation-triangle',
            title: 'Unable to schedule Viewing',
            info: response.data.message
          })
        }
      }).finally(() => {
        this.scheduling = false
      })
    },

    removePrimeProperty () {
      if (this.viewing.properties.length > 0) {
        this.viewing.primeProperty = this.viewing.properties.shift()
      } else {
        this.viewing.primeProperty = null
      }
    },

    removeProperty (property) {
      this.viewing.properties.splice(this.viewing.properties.indexOf(property), 1)
    },

    setPrimeProperty (property) {
      let index = this.viewing.properties.indexOf(property)
      this.viewing.properties[index] = { ...this.viewing.primeProperty }
      this.viewing.primeProperty = property
    },

    formatError (error) {
      return error.replace(/(?:(applicants\.(\d*)\..*?) )/, function (match, p1, p2, offset, string) {
        return parseInt(p2) + 1 + ' '
      })
    },

    setHost () {
      this.viewing.host.name = this.profile.name
      this.viewing.host.email = this.profile.email
      this.viewing.host.phone = this.profile.phone
    }
  },

  watch: {
    scheduleForNow (value) {
      if (value) {
        this.viewing.date = moment().local()
      }
    },

    profile () {
      this.setHost()
    }
  },

  mounted () {
    if (this.profile) {
      this.setHost()
    }
  }
}
</script>
